import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Link as RouterLink } from 'react-router';
import { businessToday } from '../../../shared/documents/dates';
import { useSession } from '../../../shared/session/session';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../../shared/ui/page';
import { ROLE_LABELS, useMyTasks, useWorkProjects } from './api';
import { MyTaskList } from './MyTaskList';

/**
 * My tasks (`/my-tasks`; CLR-66): every open task assigned to the caller, in every project whose
 * work they may see, grouped by when it is due, with the time remaining.
 */
export function MyTasksPage() {
  const session = useSession();
  const tasks = useMyTasks();
  const today = businessToday(session.data?.businessTimezone ?? 'Asia/Dubai');
  return (
    <Stack spacing={3}>
      <PageHeader
        title="My tasks"
        description="The open tasks assigned to you, by when they are due."
      />
      <Section title="Assigned to me">
        <Box sx={{ p: 2 }}>
          {tasks.isPending && <LoadingState label="Loading your tasks" />}
          {tasks.isError && <ErrorState message={tasks.error.message} />}
          {tasks.data?.length === 0 && (
            <EmptyState title="No open tasks" detail="Tasks assigned to you will appear here." />
          )}
          {tasks.data !== undefined && tasks.data.length > 0 && (
            <MyTaskList tasks={tasks.data} today={today} />
          )}
        </Box>
      </Section>
    </Stack>
  );
}

/** Project work (`/work`; CLR-66): the projects whose work the caller may open. */
export function WorkProjectsPage() {
  const projects = useWorkProjects();
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Project work"
        description="Boards, tasks and milestones of the projects you are a member of."
      />
      <Section title="My projects">
        {projects.isPending && <LoadingState label="Loading your projects" />}
        {projects.isError && <ErrorState message={projects.error.message} />}
        {projects.data?.length === 0 && (
          <EmptyState
            title="You are not a member of any project yet"
            detail="A project manager adds you to a project's team."
          />
        )}
        {projects.data !== undefined && projects.data.length > 0 && (
          <Table size="small" aria-label="My projects">
            <TableHead>
              <TableRow>
                <TableCell>Project</TableCell>
                <TableCell>My role</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.data.map((project) => (
                <TableRow key={project.id} hover>
                  <TableCell>
                    <Link component={RouterLink} to={`/projects/${project.id}/work`}>
                      {project.code} — {project.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {project.myRole === null ? 'Project office' : ROLE_LABELS[project.myRole]}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={project.status === 'active' ? 'Active' : 'Inactive'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </Stack>
  );
}
